// Minimal in-browser ZIP writer (stored / uncompressed, no dependencies).
// Produces a valid .zip the admin can download with telemetry_pusher.py,
// a pre-filled start_<rigKey>.bat launcher, and a README.txt.
//
// The pusher script is fetched at download time from /telemetry_pusher.py so
// the zip always contains the latest version — no embedded copy to keep in
// sync. public/telemetry_pusher.py is the shipping source of truth.

// ---- CRC32 ----
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    }
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(data) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function strToBytes(str) {
  return new TextEncoder().encode(str);
}

function u16(dv, off, val) { dv.setUint16(off, val, true); }
function u32(dv, off, val) { dv.setUint32(off, val, true); }

function makeZip(files) {
  const chunks = [];
  const central = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = strToBytes(file.name);
    const data = file.data;
    const crc = crc32(data);
    const size = data.length;

    // Local file header (30 bytes)
    const lh = new DataView(new ArrayBuffer(30));
    u32(lh, 0, 0x04034b50);
    u16(lh, 4, 20);
    u16(lh, 6, 0);
    u16(lh, 8, 0); // stored
    u16(lh, 10, 0);
    u16(lh, 12, 0x21);
    u32(lh, 14, crc);
    u32(lh, 18, size);
    u32(lh, 22, size);
    u16(lh, 26, nameBytes.length);
    u16(lh, 28, 0);

    const localHeader = new Uint8Array(lh.buffer);
    chunks.push(localHeader, nameBytes, data);

    // Central directory record (46 bytes)
    const cd = new DataView(new ArrayBuffer(46));
    u32(cd, 0, 0x02014b50);
    u16(cd, 4, 20);
    u16(cd, 6, 20);
    u16(cd, 8, 0);
    u16(cd, 10, 0);
    u16(cd, 12, 0);
    u16(cd, 14, 0x21);
    u32(cd, 16, crc);
    u32(cd, 20, size);
    u32(cd, 24, size);
    u16(cd, 28, nameBytes.length);
    u16(cd, 30, 0);
    u16(cd, 32, 0);
    u16(cd, 34, 0);
    u16(cd, 36, 0);
    u32(cd, 38, 0);
    u32(cd, 42, offset);

    central.push(new Uint8Array(cd.buffer), nameBytes);
    offset += 30 + nameBytes.length + size;
  }

  const centralChunks = [];
  let centralSize = 0;
  for (const c of central) { centralChunks.push(c); centralSize += c.length; }

  // End of central directory (22 bytes)
  const eocd = new DataView(new ArrayBuffer(22));
  u32(eocd, 0, 0x06054b50);
  u16(eocd, 4, 0);
  u16(eocd, 6, 0);
  u16(eocd, 8, files.length);
  u16(eocd, 10, files.length);
  u32(eocd, 12, centralSize);
  u32(eocd, 16, offset);
  u16(eocd, 20, 0);

  const all = [...chunks, ...centralChunks, new Uint8Array(eocd.buffer)];
  const total = all.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const c of all) { out.set(c, pos); pos += c.length; }
  return out;
}

const INGEST_URL = 'https://motion-reality-website.base44.app/functions/ingestTelemetry';
const FALLBACK_VERSION = 'v2.4';

// Fetches the latest pusher script text from the public static asset so the zip
// always contains the current version — no embedded copy to keep in sync.
// Returns the raw string so the caller can extract the [pusher vX.Y] version tag.
async function fetchPusherScriptText() {
  const resp = await fetch('/telemetry_pusher.py?t=' + Date.now());
  if (!resp.ok) throw new Error('Could not load telemetry_pusher.py (' + resp.status + ')');
  const text = await resp.text();
  // A missing static file is served as the app shell, and an interrupted fetch
  // can come back empty — either way the zip would ship a useless .py. Check the
  // body really is the pusher script and fail loudly instead.
  if (!text.trim() || !/Motion Reality/i.test(text) || /<div id="root"/i.test(text)) {
    throw new Error('The pusher script did not load correctly — please retry the download.');
  }
  return text;
}

function stripHtml(val) {
  // Strip ALL HTML/anchor tags so every value baked into the .bat is plain text.
  // Defends against editors, clipboard pastes, or rich-text rendering that
  // wrap URLs in <a href="..."> tags — which would corrupt the .bat parser
  // (< and > are redirect operators) and send Python a garbled URL/token.
  if (val == null) return '';
  return String(val).replace(/<[^>]*>/g, '');
}

function escapeBatValue(val) {
  // First strip any HTML, then escape characters the Windows .bat interpreter
  // would mangle inside a `set X=<value>` assignment: % triggers variable
  // expansion, ^ & | < > are escape / command-separator / redirect characters.
  // Doubling % and prefixing the rest with a caret makes the batch parser
  // decode them back to literals, so Python receives the exact secret value.
  return stripHtml(val)
    .replace(/%/g, '%%')
    .replace(/[\^&|<>]/g, (ch) => '^' + ch);
}

function makeBat({ rigKey, rigName, sim, token }) {
  const safeName = stripHtml((rigName || rigKey)).replace(/"/g, '');
  const tok = stripHtml(token) || 'PASTE_YOUR_LIVE_TELEMETRY_TOKEN_HERE';
  const plainUrl = stripHtml(INGEST_URL);
  const plainKey = stripHtml(rigKey);
  // Pin the sim only when one is explicitly chosen. Assetto Corsa and
  // Competizione publish identical shared memory, so a pinned launcher removes
  // any ambiguity about which of the two a rig is running.
  const simLine = sim && sim !== 'auto' ? ['set SIM=' + stripHtml(sim), ''] : [];
  return [
    '@echo off',
    'REM ================================================================',
    'REM  Motion Reality — Live Telemetry Pusher launcher (v2.4 Auto-Detect)',
    'REM  Rig: ' + plainKey + ' — ' + safeName,
    'REM  Generated by the admin Live Telemetry area.',
    'REM ================================================================',
    '',
    'set TELEMETRY_TOKEN=' + escapeBatValue(tok),
    'set INGEST_URL=' + plainUrl,
    'set RIG_KEY=' + plainKey,
    'set RIG_NAME=' + escapeBatValue(safeName),
    'set POLL_INTERVAL=0.3',
    ...simLine,
    'REM Switch to this script\'s own folder so telemetry_pusher.py and any',
    'REM relative config files resolve regardless of the launch directory',
    'REM (e.g. a shell:startup shortcut with no "Start in" property set).',
    'cd /d "%~dp0"',
    '',
    'echo.',
    'echo [Motion Reality] Starting telemetry pusher for %RIG_NAME% (%RIG_KEY%)...',
    'echo [Motion Reality] Auto-detecting sim  -  Posting to: %INGEST_URL%',
    'echo.',
    'py telemetry_pusher.py || python telemetry_pusher.py',
    '',
    'echo.',
    'echo Pusher stopped. Press any key to close...',
    'pause >nul',
  ].join('\r\n') + '\r\n';
}

function makeReadme({ rigKey, rigName, sim, token }) {
  const tok = stripHtml(token) || 'PASTE_YOUR_LIVE_TELEMETRY_TOKEN_HERE';
  const sKey = stripHtml(rigKey);
  const sName = stripHtml(rigName || rigKey);
  const sUrl = stripHtml(INGEST_URL);
  const simLabel =
    sim === 'acc' ? 'Assetto Corsa Competizione'
      : sim === 'assetto' ? 'Assetto Corsa'
        : 'Auto-detect (iRacing / Assetto Corsa / ACC)';
  return `Motion Reality — Live Telemetry Setup
======================================
Rig: ${sKey} (${sName})
Sim: ${simLabel}

WHAT'S IN THIS ZIP
------------------
  telemetry_pusher.py   — the pusher script (auto-detects whichever sim is running)
  start_${sKey}.bat      — double-click to launch (all settings pre-filled)
  README.txt            — this file

PREREQUISITES (do once per rig PC)
----------------------------------
1. Install Python 3.9+ from https://python.org
   IMPORTANT: On the first installer screen, tick "Add Python to PATH".

2. Open Command Prompt and install the dependencies:
     py -m pip install pyirsdk pyyaml requests
     (Assetto Corsa and Competizione use Windows ctypes — no extra package needed)

RUNNING
-------
1. Start your chosen simulator:
   • iRacing: Load into a session and enter the car.
   • Assetto Corsa: Click "Drive" and load onto the circuit (telemetry memory only activates once the car is on track, not in the main menu).
   • Assetto Corsa Competizione: join a session (or start a practice) and be in the car on track — ACC publishes nothing from the main menus.
2. Double-click start_${sKey}.bat.
3. The console will show "[pusher v2.4] Auto-detect active..." and hook the running sim within 2 seconds.
4. Your rig will automatically switch to LIVE on https://motionreality.co.uk/live.

CONFIG (already set in the .bat — edit only if needed)
------------------------------------------------------
  TELEMETRY_TOKEN = ${tok}
  INGEST_URL      = ${sUrl}
  RIG_KEY         = ${sKey}
  RIG_NAME        = ${sName}
  SIM             = ${sim && sim !== 'auto' ? sim : '(auto-detect)'}
  POLL_INTERVAL   = 0.3

AUTO-START AT WINDOWS BOOT (OPTIONAL)
-------------------------------------
To run the pusher automatically at boot without a technician present:

1. Copy telemetry_pusher.py and start_${sKey}.bat into:
     C:\\MotionReality\\Telemetry\\

2. Open the Windows Startup directory:
   • Press Win + R to launch the Run prompt.
   • Type shell:startup and press Enter.

3. Create a shortcut to the batch launcher:
   • In File Explorer, navigate to C:\\MotionReality\\Telemetry\\
   • Right-click start_${sKey}.bat -> Show more options (Windows 11) -> Create shortcut.
   • Move the shortcut into the shell:startup directory.

4. Configure the shortcut:
   • Right-click the shortcut in shell:startup -> Properties.
   • Set the "Start in" field to:
       C:\\MotionReality\\Telemetry\\
     (Required so the batch script resolves relative paths for
      telemetry_pusher.py and local config files.)
   • Change the "Run:" dropdown from "Normal window" to "Minimized".
   • Click Apply -> OK.

WHAT HAPPENS AT BOOT
   Windows triggers the shortcut automatically on login, launching the
   process minimized to the taskbar (no kiosk / sim display interruption).
   The script runs auto_detect_loop() at ~2s polling intervals, hooks the
   memory stream when a session starts, and streams packets to the central
   ingestion endpoint.

TROUBLESHOOTING
---------------
- "python is not recognized":
    Ensure Python was installed with "Add Python to PATH" checked, or run "py --version" in Command Prompt to verify the launcher.
- Script stuck on "waiting for iRacing, Assetto Corsa or ACC":
    Ensure you are sitting in the car on track. The Assetto Corsa games do not broadcast telemetry while in the showroom or car-selection menus.
- ACC rig shows as "Assetto Corsa":
    Both games publish the same shared memory. Pick "Assetto Corsa Competizione" in the Sim box on the Live Telemetry page and re-download this launcher to pin it.
- "Unauthorized" errors:
    The TELEMETRY_TOKEN in the .bat file does not match the app secret.
`;
}

export async function downloadRigZip({ rigKey, rigName, sim, token }) {
  const safeKey = (rigKey || 'rig').replace(/[^a-zA-Z0-9_]/g, '_');
  const batName = `start_${safeKey}.bat`;
  const scriptText = await fetchPusherScriptText();
  const scriptData = strToBytes(scriptText);
  const versionMatch = scriptText.match(/\[pusher\s+v([\d.]+)\]/i);
  const version = versionMatch ? 'v' + versionMatch[1].replace(/[^0-9.]/g, '') : FALLBACK_VERSION;
  const files = [
    { name: 'telemetry_pusher.py', data: scriptData },
    { name: batName, data: strToBytes(makeBat({ rigKey: safeKey, rigName, sim, token })) },
    { name: 'README.txt', data: strToBytes(makeReadme({ rigKey: safeKey, rigName, sim, token })) },
  ];
  const zipBytes = makeZip(files);
  // Never hand over an archive that holds nothing.
  if (!zipBytes.length) throw new Error('The setup archive came out empty — please retry the download.');
  const blob = new Blob([zipBytes], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `motionreality_telemetry_${safeKey}_${version}.zip`;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Leave the link in the DOM until the browser has picked the download up, and
  // hold the blob URL well past that — tearing either down too early truncates
  // the file the technician receives.
  setTimeout(() => a.remove(), 10000);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}